import {
	WorkfrontBundleObjectRole,
	WorkfrontComputeFn,
	WorkfrontLookup,
	WorkfrontMappingKind,
	WorkfrontMappingRule,
	WorkfrontSourceKind,
} from '@atlas/types'
import { z } from 'zod'
import { workfrontIdSchema } from './workfront-adapter.schema.js'

// ─── Draft rows: what an admin saves, one per mapping kind ──────────────────────

const ruleParamsSchema = z
	.object({
		firstValue: z.boolean().optional(),
		lookup: z.enum(WorkfrontLookup).optional(),
		axis: z
			.string()
			.regex(/^[a-z][a-z0-9_]*$/, 'must be an axis code')
			.optional(),
		detailParameterIds: z.array(workfrontIdSchema).max(50).optional(),
		fn: z.enum(WorkfrontComputeFn).optional(),
		parameterIds: z.array(workfrontIdSchema).max(20).optional(),
		host: z.enum(['workfront', 'vault']).optional(),
		entity: z.literal('campaign').optional(),
		yesValue: z.string().trim().min(1).max(200).optional(),
		priority: z.number().int().min(0).max(100).optional(),
	})
	.strict()

/** A field mapping as a draft change carries it. */
export const workfrontFieldMappingRowSchema = z
	.object({
		sourceRole: z.enum(WorkfrontBundleObjectRole),
		sourceKind: z.enum(WorkfrontSourceKind),
		sourceParameterId: workfrontIdSchema.nullable(),
		sourceField: z
			.string()
			.regex(/^[A-Za-z][A-Za-z0-9:]*$/, 'must be a Workfront field name')
			.nullable(),
		fallbackParameterId: workfrontIdSchema.nullable(),
		targetPath: z.string().regex(/^[a-z]+(\.[A-Za-z_]+)+$/, 'must be a target path'),
		rule: z.enum(WorkfrontMappingRule),
		ruleParams: ruleParamsSchema,
		enabled: z.boolean(),
		notes: z.string().trim().max(1000).nullable(),
	})
	.strict()
	.refine((row) => (row.sourceKind === WorkfrontSourceKind.PARAMETER ? !!row.sourceParameterId : !!row.sourceField), {
		message: 'a parameter source needs sourceParameterId, a standard source needs sourceField',
		path: ['sourceKind'],
	})

/** An asset-type row as a draft change carries it. */
export const workfrontAssetTypeMappingRowSchema = z
	.object({
		classificationValue: z.string().trim().min(1).max(200),
		assetDetailParameterId: workfrontIdSchema.nullable(),
		assetDetailValue: z.string().trim().min(1).max(200).nullable(),
		assetTypeCode: z.string().trim().min(1).max(20),
	})
	.strict()

/** A lookup row as a draft change carries it. */
export const workfrontLookupRowSchema = z
	.object({
		lookup: z.enum(WorkfrontLookup),
		sourceValue: z.string().trim().min(1).max(200),
		targetValue: z.string().trim().min(1).max(200),
	})
	.strict()

/** A Workfront label mapped to an ATLAS value, as a draft change carries it. */
export const workfrontValueMappingRowSchema = z
	.object({
		axis: z.string().regex(/^[a-z][a-z0-9_]*$/, 'must be an axis code'),
		label: z.string().trim().min(1).max(300),
		valueId: z.string().regex(/^\d+$/, 'must be a value id'),
		geoValueId: z.string().regex(/^\d+$/, 'must be a value id').nullable(),
	})
	.strict()

/** The row schema of each mapping kind. */
export const workfrontMappingRowSchemas = {
	[WorkfrontMappingKind.FIELD]: workfrontFieldMappingRowSchema,
	[WorkfrontMappingKind.ASSET_TYPE]: workfrontAssetTypeMappingRowSchema,
	[WorkfrontMappingKind.LOOKUP]: workfrontLookupRowSchema,
	[WorkfrontMappingKind.VALUE]: workfrontValueMappingRowSchema,
} as const

// ─── Requests ───────────────────────────────────────────────────────────────────

const revisionSchema = z.number().int().min(0)

/** `PUT /admin/workfront/mappings/draft/{kind}/{key}`: the row, on the draft revision it was edited from. */
export const workfrontDraftSaveSchema = z
	.object({ revision: revisionSchema, row: z.record(z.string(), z.unknown()) })
	.strict()

/** `POST /admin/workfront/mappings/draft/publish`. */
export const workfrontMappingPublishSchema = z
	.object({ revision: revisionSchema, summary: z.string().trim().min(3).max(300) })
	.strict()

/** `POST /admin/workfront/mappings/quick-publish`: one row, published on its own (an error's Fix). */
export const workfrontQuickPublishSchema = z
	.object({
		kind: z.enum(WorkfrontMappingKind),
		row: z.record(z.string(), z.unknown()),
		summary: z.string().trim().min(3).max(300).optional(),
	})
	.strict()

/** `POST /admin/workfront/mappings/versions/{version}/republish`. */
export const workfrontRepublishSchema = z.object({ summary: z.string().trim().min(3).max(300).optional() }).strict()

/** `POST /admin/workfront/mappings/unmapped-fields/{parameterId}/ignore`. */
export const workfrontUnmappedIgnoreSchema = z.object({ reason: z.string().trim().min(3).max(1000) }).strict()

/** A Workfront ID in a seed, with what identifies it on another environment. */
const describedIdSchema = z
	.object({
		id: workfrontIdSchema,
		objCode: z.string().regex(/^[A-Z]{3,8}$/, 'must be a Workfront object code'),
		name: z.string().trim().min(1),
		form: z.string().trim().min(1).optional(),
	})
	.strict()

/** A value mapping in a seed: the value named by its code, which is the same on every environment. */
const seedValueSchema = z
	.object({
		axis: z.string().regex(/^[a-z][a-z0-9_]*$/, 'must be an axis code'),
		valueCode: z.string().trim().min(1).max(200),
		label: z.string().trim().min(1).max(300),
	})
	.strict()

/** `POST /admin/workfront/mappings/import`: a seed keyed by description, and the admin's confirmations. */
export const workfrontMappingImportSchema = z
	.object({
		seed: z
			.object({
				ids: z.array(describedIdSchema).max(2000),
				fields: z.array(workfrontFieldMappingRowSchema).max(2000).default([]),
				assetTypes: z.array(workfrontAssetTypeMappingRowSchema).max(2000).default([]),
				lookups: z
					.array(workfrontLookupRowSchema)
					.max(5000)
					.refine((rows) => !rows.some((row) => row.lookup === WorkfrontLookup.REGION_TO_PM), {
						message: `${WorkfrontLookup.REGION_TO_PM} names users of one stage: enter the PM rules on each stage instead`,
					})
					.default([]),
				values: z.array(seedValueSchema).max(20000).default([]),
			})
			.strict(),
		/** Seed ID → the local ID the admin confirmed, for each one that differs. */
		confirmations: z.record(workfrontIdSchema, workfrontIdSchema).default({}),
		revision: revisionSchema.optional(),
	})
	.strict()

export type WorkfrontFieldMappingRowInput = z.infer<typeof workfrontFieldMappingRowSchema>
export type WorkfrontAssetTypeMappingRowInput = z.infer<typeof workfrontAssetTypeMappingRowSchema>
export type WorkfrontLookupRowInput = z.infer<typeof workfrontLookupRowSchema>
export type WorkfrontValueMappingRowInput = z.infer<typeof workfrontValueMappingRowSchema>
export type WorkfrontDraftSaveInput = z.infer<typeof workfrontDraftSaveSchema>
export type WorkfrontMappingPublishInput = z.infer<typeof workfrontMappingPublishSchema>
export type WorkfrontQuickPublishInput = z.infer<typeof workfrontQuickPublishSchema>
export type WorkfrontRepublishInput = z.infer<typeof workfrontRepublishSchema>
export type WorkfrontUnmappedIgnoreInput = z.infer<typeof workfrontUnmappedIgnoreSchema>
export type WorkfrontMappingImportInput = z.infer<typeof workfrontMappingImportSchema>
